import { Check } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useT } from '@/lib/i18n';

/**
 * The tick box.
 *
 * Deliberately a real `<button>` with `aria-pressed`, not a styled `<input>`:
 * it triggers a mutation rather than holding form state, and this keeps the
 * keyboard and screen-reader behaviour honest.
 */
export function TaskCheckbox({
  checked,
  onToggle,
  label,
  disabled,
  title,
  className,
}: {
  checked: boolean;
  onToggle: () => void;
  label: string;
  disabled?: boolean;
  /** Why it is disabled, shown on hover. */
  title?: string;
  className?: string;
}) {
  const t = useT();
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={
        checked
          ? t('task.reopenNamed', { title: label })
          : t('task.completeNamed', { title: label })
      }
      disabled={disabled}
      {...(title ? { title } : {})}
      onClick={(event) => {
        // The row itself opens the detail; ticking must not do both.
        event.stopPropagation();
        onToggle();
      }}
      className={cn(
        'relative flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px]',
        // A phone is operated with a thumb, not a cursor. The mark grows to
        // 24px below `sm`, and the *hit area* grows further still: the
        // pseudo-element extends the target to ~44px in every direction
        // without moving the mark or the row's layout. Ticking something
        // should never require aiming.
        // Touch: 28px with a 2px ink border. The eye measures *weight* — a
        // hairline box reads as tiny however large it is drawn.
        'max-sm:h-7 max-sm:w-7 max-sm:rounded-lg max-sm:border-2',
        'touch:h-7 touch:w-7 touch:rounded-lg touch:border-2',
        'before:absolute before:-inset-3 before:content-[""] max-sm:before:-inset-2 touch:before:-inset-2',
        'border transition-surface disabled:opacity-40 disabled:cursor-not-allowed',
        checked
          ? 'border-success bg-success text-white'
          : 'border-border-strong hover:border-accent hover:bg-accent-subtle max-sm:border-content-muted touch:border-content-muted',
        className,
      )}
    >
      {checked ? (
        <Check className="h-3 w-3 max-sm:h-[18px] max-sm:w-[18px] touch:h-[18px] touch:w-[18px]" strokeWidth={3} aria-hidden />
      ) : null}
    </button>
  );
}
